/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationError,
  NavigationStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';

import { constant, defer } from 'lodash';

import { BuilderUserContext, BuilderUserData, MaintenanceService, SmsService } from '@gh/builder-shared';
import {
  ApplicationView,
  JasperServerStatus,
  Language,
  TodoRestService,
  UserRestService,
  userSystemRoleFromBitSet,
} from '@gh/builder-shared-data';
import { SystemInfoDialogComponent } from '@gh/common-internal-shared';
import {
  ApplicationComponentState,
  DictionaryService,
  EnumDictionary,
  getRouterEventName,
  HelpProvider,
  IntercomService,
} from '@gh/common-shared';

import { UserData } from '@gh/common-shared-data';
import {
  ENV_BASE_URL,
  MAINTENANCE_CHECK_ENABLED,
  MAINTENANCE_CHECK_INTERVAL,
  TODO_CHECK_ENABLED,
  TODO_CHECK_INTERVAL,
} from '@gh/config';
import { AuthService, AuthState } from '@gh/core-auth';
import { MessagePushService } from '@gh/core-messages';
import { Locale } from '@gh/core-mls';

import {
  Confirmation,
  DialogService,
  NotificationService,
  WaitingContext,
  WaitingMode,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { openInNewWindow, toString } from '@gh/core-util';
import { concat$, defer$, EMPTY$, interval$, of$ } from '@gh/rx';
import { catchError$, filter$, first$, mergeMap$, startWith$, switchMap$, tap$ } from '@gh/rx/operators';
import { Observable, Subscription } from 'rxjs';
import { jcDetailsDialogRoute } from '../jc-details-dialog/jc-details-dialog.component';
import { licenseAgreementDialogRoute } from '../license-agreement-dialog/license-agreement-dialog.component';
import { userProfileSignatureDialog, UserProfileSignatureDialogConfig } from '../user-profile-signature';
import {
  userProfileValidationDialogRoute
} from '../user-profile-validation-dialog/user-profile-validation-dialog.route';
import { userProfilePhotoDialog, UserProfilePhotoDialogConfig } from '../user-profile-photo';
import { mobileQRCodeDialogRoute } from '../mobile-qr-code-dialog/mobile-qr-code-dialog.component';

@Component({
  selector: 'gh-builder-app',
  template: require('./builder-app.component.html'),
  styles: [require('./builder-app.component.scss')],
  providers: [TodoRestService],
})
export class BuilderAppComponent implements OnInit, OnDestroy, AfterViewInit {
  title: string;
  subtitle: string;
  switched: boolean;
  languages: EnumDictionary<Language>;
  user: UserData;
  hasNewToDo = false;
  smsCount: number;
  isSetupMaintenanceTime = false;
  numberOfMinutesBeforeMaintenance: number;
  jasperReportsUrl: string;
  planBankUrl: string;
  talentLmsUrl: string;
  acceptanceUsePolicyDocumentUrl: string;
  intranetUrl: string;
  payPalUrl: string;
  jasperCommercialStatus: string;
  jasperCommercialUrl: string;
  isOpenedMaintenanceDialog = false;
  profilePictureUrl: any = '';
  interval: any;

  private subscription?: Subscription;
  private wsSubscription: Subscription;

  /**
   * Subscription is responsible for dialog actions after user's logout
   */
  private logout$subscription: Subscription;

  constructor(public userContext: BuilderUserContext,
              private authService: AuthService,
              private dialogService: DialogService,
              private dictionaryService: DictionaryService,
              public router: Router,
              private applicationComponentState: ApplicationComponentState,
              private service: TodoRestService,
              private userService: UserRestService,
              private helpProvider: HelpProvider,
              private notificationPushService: MessagePushService,
              private notificationService: NotificationService,
              private waitings: WaitingService,
              private maintenanceService: MaintenanceService,
              private smsService: SmsService,
              private intercomService: IntercomService,
              private waitingContext: WaitingContext) {
  }

  get canOpenBiReports(): boolean {
    return this.userContext.corporateView;
  }

  get canOpenSms(): boolean {
    return this.userContext.officeView;
  }

  get language(): Language {
    return <any>this.user.locale.toStandardString();
  }

  get maintenanceMsg(): string {
    return `System will be turned off for maintenance in ${this.numberOfMinutesBeforeMaintenance} minutes`;
  }

  ngOnInit(): void {
    if (!this.userContext.isInitialized()) {
      return;
    }

    this.initApplicationView();
    if (this.userContext.officeView) {
      this.initWebSocket();
    }
    this.updateUserData(<BuilderUserData>this.userContext.data);
    this.dictionaryService.getByEnum(Language).subscribe((languages) => this.languages = languages);

    this.notificationPushService.onMessage.subscribe((message) => {
      this.notificationService.message(message);
    });

    // display waiting effect while loading of lazy modules
    this.router
      .events.pipe(
      // tslint:disable-next-line:no-console
      tap$((event) => {
        if (process.env.NODE_ENV === 'development') {
          // tslint:disable-next-line:no-console
          console.debug('router event: %s', getRouterEventName(event));
        }
      }),
      filter$((event) => event instanceof RouteConfigLoadStart),
      mergeMap$(({ route: startRoute }: RouteConfigLoadStart) => {
        const process = this.waitingContext.start(WaitingMode.Background, 'router');
        return this.router.events.pipe(
          filter$((event) =>
            // take into account the next event only if it is end of route loading or route load error
            event instanceof RouteConfigLoadEnd && event.route === startRoute ||
            event instanceof NavigationStart ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError),
          first$(),
          tap$(() => process.stop()));
      }))
      .subscribe();

    this.subscription = this.userContext
      .onData
      .subscribe((user) => this.updateUserData(<BuilderUserData>user));

    this.wsSubscription = this.smsService.smsCount$.subscribe((res: number) => {
      this.smsCount = res;
    });

    this.logout$subscription = this.authService.onAuthState.subscribe((authState: AuthState) => {
      if (authState === AuthState.LogggedOut) {
        this.dialogService.closeDialogs();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.logout$subscription) {
      this.logout$subscription.unsubscribe();
    }
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (!this.userContext.isInitialized()) {
      return;
    }

    // we have to run this code in another frame because angular forbids updates to the view after it has been composed
    defer(() => {
      // validate if it is allowed to use application for the current user
      const { validationResult } = <BuilderUserData>this.userContext.data;

      if (validationResult && validationResult.paymentStatus) {
        this.dialogService.openWithInput(userProfileValidationDialogRoute, validationResult);
      } else {
        this.router.initialNavigation();
        this.listenForMaintenanceTime();

        if (!this.userContext.isSwitched()) {
          this.listenForNewToDo();
        }
        this.showDialogs();
      }
    });
  }

  changePassword(): void {
    this.userContext.changePassword();
  }

  logout(): void {
    this.userContext.logout();
  }

  showSystemInfo(): void {
    this.dialogService.open(SystemInfoDialogComponent);
  }

  openBugRequestDialog(): void {
    this.intercomService.showIntercom();
  }

  onManualOpen(): void {
    const helpId = this.helpProvider.getCurrent();

    this.helpProvider.open(helpId);
  }

  onLicenseOpen(): void {
    this.dialogService.openWithInput(licenseAgreementDialogRoute, { viewMode: true });
  }

  onSandboxOpen(): void {
    const { origin, pathname, search } = location;
    openInNewWindow(`${origin}/sandbox${pathname}${search}`);
  }

  onOpenJc(): void {
    this.dialogService.openWithInput(jcDetailsDialogRoute, {});
  }

  onOpenMobileQRCode(): void {
    this.dialogService.openWithInput(mobileQRCodeDialogRoute, {});
  }

  get isDisabledJasperCommercial(): boolean {
    return this.userContext.masterView && this.jasperCommercialStatus !== JasperServerStatus.Active.toUpperCase();
  }

  toDoClick(): void {
    this.hasNewToDo = false;
    this.router.navigateByUrl('/todo');
  }

  smsClick(): void {
    this.router.navigateByUrl('/sms');
  }

  changeLanguage(language: string): void {
    if (this.user.locale.toStandardString() === language) {
      return;
    }

    const leave$ = this.applicationComponentState.hasDirtyChanges() ?
      this.dialogService.confirmation(yesNoConfirmation('msg_change_language_unsaved_data')) : of$(Confirmation.Yes);

    leave$.subscribe((confirmation) => {
      if (yesAnswer(confirmation)) {
        this.applicationComponentState.setIgnoreDirty();
        this.userContext.changeLocale(Locale.createFromStandard(language));
      }
    });
  }

  // toastNotification(message: any): void {
  //   const newToastNotification = new ToastNotificationInitializer();
  //   newToastNotification.setTitle(`SMS From: ${message.contactName ? message.contactName : message.cellPhone}`);
  //   newToastNotification.setMessage(`${message.message}`);
  //
  //   newToastNotification.openToastNotification$();
  // }

  uploadSignatureDialog(): void {
    this.dialogService.openWithInput(
      userProfileSignatureDialog,
      <UserProfileSignatureDialogConfig>{},
    );
  }

  uploadPhotoDialog(): void {
    this.dialogService.openWithInput(
      userProfilePhotoDialog,
      <UserProfilePhotoDialogConfig>{},
    ).afterClosed().pipe(
      filter$(Boolean),
      switchMap$(() =>
        this.userService.getPhoto(this.user.id, 'SMALL').pipe(this.waitings.operator())))
      .subscribe((res) => {
        this.profilePictureUrl = res ? res.location : '';
    });
  }

  private updateUserData(user: BuilderUserData): void {
    this.user = user;
    this.profilePictureUrl = user.profilePicture ? user.profilePicture.location : '';
    this.jasperReportsUrl = user.jasperReportsUrl;
    this.jasperCommercialStatus = user.jasperCommercialStatus;
    this.jasperCommercialUrl = user.jasperCommercialUrl;
    this.planBankUrl = user.planBankUrl;
    this.talentLmsUrl = user.talentLmsUrl;
    this.intranetUrl = user.intranetUrl;
    this.payPalUrl = user.payPalUrl;
    this.acceptanceUsePolicyDocumentUrl = user.acceptanceUsePolicyDocumentUrl;
    this.initUpdateIntercom(user);
  }

  private initApplicationView(): void {
    switch (this.userContext.applicationView) {
      case ApplicationView.Corporate:
        this.title = 'lbl_menu_corporate_view';
        this.subtitle = '';
        break;
      case ApplicationView.Master:
        this.title = 'lbl_master_zone';
        if (this.userContext.masterZone) {
          this.subtitle = this.userContext.masterZone.name;
        }
        break;
      case ApplicationView.Office:
        this.title = 'lbl_office_view';
        if (this.userContext.office) {
          this.subtitle = this.userContext.office.name;
        }
        break;
      default:
        break;
    }

    this.switched = this.userContext.isSwitched();
  }

  private initWebSocket(): void {
    if (this.userContext.officeView) {
      this.smsService.connect();
    }
  }

  private listenForNewToDo(): void {
    if (TODO_CHECK_ENABLED) {
      interval$(TODO_CHECK_INTERVAL).pipe(
        startWith$(0),
        switchMap$(() => this.service.checkNewToDo().pipe(catchError$(constant(EMPTY$)))))
        .subscribe((count: number) => {
          this.hasNewToDo = this.hasNewToDo || count > 0;
        });
    }
  }

  private listenForMaintenanceTime(): void {
    if (MAINTENANCE_CHECK_ENABLED) {
      this.maintenanceService.connect(
        (minutesBeforeMaintenance: number) => {
          this.numberOfMinutesBeforeMaintenance = minutesBeforeMaintenance;
          this.isSetupMaintenanceTime = minutesBeforeMaintenance > 0;
          if (this.isSetupMaintenanceTime) {
            this.interval = setInterval(() => {
              this.numberOfMinutesBeforeMaintenance--;
              if (this.numberOfMinutesBeforeMaintenance === 0) {
                clearInterval(this.interval);
              }},                       MAINTENANCE_CHECK_INTERVAL);

          }},
      );
      // interval$(MAINTENANCE_CHECK_INTERVAL).pipe(
      //   startWith$(0),
      //   switchMap$(() => this.userProfileRest.getMaintenanceInfo().pipe(catchError$(constant(EMPTY$)))))
      //   .subscribe(({ minutesBeforeMaintenance, maintenanceConfirmed }) => {
      //     this.numberOfMinutesBeforeMaintenance = minutesBeforeMaintenance;
      //     // tslint:disable-next-line:no-magic-numbers
      //     this.isSetupMaintenanceTime = minutesBeforeMaintenance <= 15 && minutesBeforeMaintenance > 0;
      //     if (minutesBeforeMaintenance > 0) {
      //       if (!maintenanceConfirmed && !this.isOpenedMaintenanceDialog) {
      //         this.isOpenedMaintenanceDialog = true;
      //         this.dialogService.warning('lbl_maintenance_turn_off', {
      //           x: toString(this.numberOfMinutesBeforeMaintenance),
      //         }).subscribe(() => {
      //           this.userService.confirmMaintenance().subscribe(() => this.waitings.operator());
      //         });
      //       }
      //     }
      //   });
    }
  }

  private showDialogs(): void {
    const dialogFactories: RetFn<Observable<any>>[] = [];
    if (this.userContext.sandbox) {
      dialogFactories.push(() => this.dialogService.warning('msg_sandbox_mode'));
    }
    const data = <BuilderUserData>this.userContext.data;
    if (data.isCalendarEmpty) {
      dialogFactories.push(() => this.dialogService.warning('msg_calendar_empty', {
        days: toString(data.calendarWarningDays),
      }));
    }

    concat$(...dialogFactories.map(defer$)).subscribe();
  }

  // TODO: refactor with intercom service
  private initUpdateIntercom(user: BuilderUserData): void {
    const getCountry = (): string => {
      const baseUrl = ENV_BASE_URL.toString();
      // @ts-ignore
      switch (true) {
        case baseUrl.includes('-au') || baseUrl.includes('.au'):
          return 'au';
        case baseUrl.includes('-nz') || baseUrl.includes('.nz'):
          return 'nz';
        case baseUrl.includes('-us') || baseUrl.includes('.com'):
          return 'us';
        default:
          return 'us';
      }
    };
    const appId = this.intercomService.isProd ? 'w2lvu81a' : 'o9y8syt5';
    const intercomSettings = {
      app_id: appId,
      name: user.fullName,
      email: user.login,
      user_id: `${getCountry()}-${user.id}`,
      user_name: user.fullName,
      user_email: user.login,
      user_type: user.type,
      user_office_name: user.office?.name,
      user_master_zone: user.masterZone?.name,
      user_country: getCountry(),
      user_system_roles: userSystemRoleFromBitSet(user.systemRoles),
      user_hash: user.serviceRequestFormHMAC,
    };
    const intercom = window['Intercom'];
    if (typeof intercom === 'function') {
      intercom('update', intercomSettings);
      return;
    }
    if (document.getElementById('intercomScript')) {
      return;
    }
    const  scriptNode = document.createElement('script');
    scriptNode.type = 'text/javascript';
    scriptNode.async = true;
    scriptNode.src = `https://widget.intercom.io/widget`;
    scriptNode.id = 'intercomScript';
    scriptNode.addEventListener('load', () => {
      setTimeout(() => {
        const intercomBoot = window['Intercom'];
        window.localStorage.setItem('intercom_boot', new Date().getTime().toString());
        window.addEventListener('storage', (event) => {
         if (event.key === 'intercom_boot' && event.newValue === null) {
           window['Intercom']('shutdown');
         }
        });
        intercomBoot('boot', intercomSettings);
      },         0);
    });
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(scriptNode, firstScript);
    }
  }
}
